import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CustomerMailDialog } from "@/components/CustomerMailDialog";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ExternalLink, FileDown, Mail, Link2, Copy, UploadCloud, Activity } from "lucide-react";

import { WORKFLOW, statusMap, downloadBlob } from "@/lib/workflow";
import { DeclineDialog } from "@/components/DeclineDialog";
import { ConfirmPriceDialog } from "@/components/ConfirmPriceDialog";
import { InvoiceCard } from "@/components/InvoiceCard";
const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "–");
const d = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "–");

function Card({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Pager({ ids, id, go }) {
  const idx = ids.indexOf(id);
  if (idx < 0) return null;
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => go(ids[idx - 1])} data-testid="adp-prev"><ChevronLeft size={16} /> Vorheriger</Button>
      <span className="text-sm text-slate-500 px-2" data-testid="adp-pager-pos">{idx + 1} / {ids.length}</span>
      <Button variant="outline" size="sm" disabled={idx === ids.length - 1} onClick={() => go(ids[idx + 1])} data-testid="adp-next">Nächster <ChevronRight size={16} /></Button>
    </div>
  );
}

function StatusCard({ data, canEdit, onSet, cfg }) {
  return (
    <Card title={cfg.statusLabel} testid="adp-status-card">
      <div className="flex gap-1.5 flex-wrap">
        {cfg.statuses.map(([k, l, on]) => (
          <button key={k} type="button" disabled={!canEdit} onClick={() => data.du_status !== k && onSet(k)} data-testid={`adp-du-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${data.du_status === k ? on : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"} disabled:opacity-60`}>{l}</button>
        ))}
      </div>
      {data.updated_at && <div className="text-xs text-slate-400 mt-2">geändert {dt(data.updated_at)} · {data.updated_by}</div>}
      <div className="mt-4 text-sm">
        <div className="text-xs text-slate-500">Anzeigen-Artikel</div>
        {data.article ? <div className="mt-0.5" data-testid="adp-article">{data.article.title}{data.article.drive_link && <a href={data.article.drive_link} target="_blank" rel="noreferrer" className="ml-1.5 text-blue-600 inline-flex items-center gap-0.5">Drive <ExternalLink size={11} /></a>}</div> : <div className="mt-0.5 text-slate-400">noch nicht angelegt</div>}
      </div>
      {data.reminder_sent_at && <div className="text-xs text-slate-500 mt-2">Auto-Erinnerung: {dt(data.reminder_sent_at)}</div>}
    </Card>
  );
}

function UploadCard({ data, onCreate, onDownload }) {
  const u = data.upload;
  const copy = () => { navigator.clipboard.writeText(u.link); toast.success("Link kopiert"); };
  return (
    <Card title="Upload-Link für die Druckunterlage" testid="adp-upload-card">
      {u.uploaded_at ? (
        <div className="text-sm">
          <div className="flex items-center gap-2 text-emerald-700 font-medium"><UploadCloud size={16} /> Hochgeladen am {dt(u.uploaded_at)}</div>
          <div className="text-slate-600 mt-1 truncate" data-testid="adp-upload-file">{u.filename} · {(u.size / 1048576).toFixed(1)} MB</div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={onDownload} data-testid="adp-upload-download"><FileDown size={14} className="mr-1.5" /> PDF herunterladen</Button>
            {u.drive_link && <a href={u.drive_link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink size={14} className="mr-1.5" /> In Drive</Button></a>}
          </div>
          <div className="text-xs text-slate-400 mt-2">Der Link ist nach dem Upload gesperrt.</div>
        </div>
      ) : u.link ? (
        <div className="text-sm">
          <div className="flex gap-2">
            <input readOnly value={u.link} className="flex-1 min-w-0 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-slate-50" data-testid="adp-upload-link" />
            <Button size="sm" variant="outline" onClick={copy} data-testid="adp-upload-copy"><Copy size={14} /></Button>
          </div>
          <div className={`text-xs mt-2 ${u.expired ? "text-red-600" : "text-slate-500"}`} data-testid="adp-upload-expiry">
            {u.expires_at ? `${u.expired ? "Abgelaufen am" : "Gültig bis"} ${d(new Date(new Date(u.expires_at) - 1))}` : "Gültig, solange die Ausgabe aktiv ist"} · nur PDF bis 50 MB
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-500">
          Noch kein Link erzeugt. Er wird automatisch angelegt, wenn eine Mail mit dem Platzhalter <code className="text-xs bg-slate-100 px-1 rounded">{"{{upload_link}}"}</code> versendet wird.
          <div><Button size="sm" variant="outline" className="mt-3" onClick={onCreate} data-testid="adp-upload-create"><Link2 size={14} className="mr-1.5" /> Link jetzt erzeugen</Button></div>
        </div>
      )}
    </Card>
  );
}

function MailRow({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-sm text-slate-800"><Mail size={13} className="inline mr-1.5 text-blue-600" />Mail: {m.subject}</span>
        <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <iframe title={m.subject} srcDoc={m.html || "<p>Kein Mailtext gespeichert.</p>"} sandbox="" className="w-full h-72 mt-2 border border-slate-200 rounded-md bg-white" />}
    </div>
  );
}

function Timeline({ data }) {
  const items = [
    ...data.activities.map((a) => ({ at: a.at, by: a.by, a })),
    ...data.mails.map((m) => ({ at: m.created_at, by: m.sent_by, m })),
  ].sort((x, y) => (y.at || "").localeCompare(x.at || ""));
  return (
    <Card title={`Aktivitäten zu dieser Ausgabe (${items.length})`} testid="adp-timeline">
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="border-l-2 border-slate-200 pl-3" data-testid={`adp-event-${i}`}>
            <div className="text-xs text-slate-500">{dt(it.at)}{it.by ? ` · ${it.by}` : ""}</div>
            {it.m ? <MailRow m={it.m} /> : <div className="text-sm text-slate-800"><Activity size={13} className="inline mr-1.5 text-slate-400" />{it.a.text}</div>}
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-slate-400">Noch keine Aktivitäten.</li>}
      </ol>
    </Card>
  );
}

function HistoryCard({ history, current, type }) {
  const LBL = statusMap(type);
  return (
    <Card title="Verlauf über alle Ausgaben" testid="adp-history">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {history.map((h) => (
            <tr key={h.issue_id} className={h.issue_id === current ? "bg-slate-50" : ""}>
              <td className="py-2 pr-2">{h.issue}{h.archived && <span className="text-xs text-slate-400"> (Archiv)</span>}</td>
              <td className="py-2 text-right"><span className={`text-[11px] font-semibold rounded px-2 py-0.5 ${(LBL[h.du_status] || LBL.offen)[1]}`}>{(LBL[h.du_status] || LBL.offen)[0]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function AdWorkflowPartner({ type = "partner" }) {
  const cfg = WORKFLOW[type];
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [issueId, setIssueId] = useState(sp.get("issue") || "");
  const [ids, setIds] = useState(location.state?.ids || []);
  const [data, setData] = useState(null);
  const [mailFor, setMailFor] = useState(null);
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.permissions?.[cfg.area] === "edit";
  const canMail = !!user?.is_chef || canEdit;

  useEffect(() => {
    if (issueId && location.state?.ids) return;
    api.get("/customers-workflow", { params: { type, ...(issueId ? { issue_id: issueId } : {}) } }).then((r) => {
      if (!issueId && r.data.issue) setIssueId(r.data.issue.id);
      if (!location.state?.ids) setIds(r.data.rows.map((x) => x.id));
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [issueId, location.state, type]);

  const load = useCallback(() => {
    if (!issueId) return;
    api.get(`/customers-workflow/${issueId}/${id}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e.response?.data?.detail)); navigate(cfg.path); });
  }, [issueId, id, navigate, cfg.path]);
  useEffect(() => { load(); }, [load]);

  const go = (nid) => { setData(null); navigate(`${cfg.path}/${nid}?issue=${issueId}`, { state: { ids } }); };
  const [decline, setDecline] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const setDu = async (status, extra = {}) => {
    if (status === "abgesagt" && type === "bezahlkunde" && extra.send_mail === undefined) { setDecline(true); return; }
    if (status === "zugesagt" && type === "bezahlkunde" && extra.final_price === undefined) { setConfirmOpen(true); return; }
    try {
      await api.put(`/customers-workflow/${issueId}/${id}`, { du_status: status, ...extra });
      toast.success(status === cfg.articleOn ? "Status geändert – Anzeigen-Artikel angelegt" : "Status geändert");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createLink = async () => {
    try { await api.post(`/customers-workflow/${issueId}/${id}/upload-link`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-slate-400">Lädt…</div>;
  const c = data.customer;
  const contact = [c.salutation, c.title, c.first_name, c.last_name].filter(Boolean).join(" ");
  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="ad-workflow-partner-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <Link to={cfg.path} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="adp-back"><ArrowLeft size={15} /> {cfg.title}</Link>
        <Pager ids={ids} id={id} go={go} />
      </div>
      <div className="flex flex-col-reverse md:flex-row md:items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="adp-name">{c.name}</h1>
          <p className="text-slate-500 mt-1">{data.issue.name}{data.issue.redaktionsschluss ? ` · Redaktionsschluss ${d(data.issue.redaktionsschluss)}` : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0 justify-end self-end md:self-start">
          <Button variant="outline" size="sm" onClick={() => downloadBlob(`/customers-workflow-activity/${id}/pdf`, {}, `Aktivitaeten_${c.name}.pdf`)} data-testid="adp-activity-pdf"><FileDown size={15} className="mr-1.5" /> Aktivitäten-PDF</Button>
          <Link to={`${cfg.detailPath}/${id}`}><Button variant="outline" size="sm" data-testid="adp-master-data">Stammdaten</Button></Link>
          <Button size="sm" disabled={!c.email || !canMail || (c.type === "partner" && !c.ad_format_id)} onClick={() => setMailFor([c])} className="bg-blue-600 hover:bg-blue-700" data-testid="adp-mail"><Mail size={14} className="mr-1.5" /> E-Mail senden</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="space-y-5">
          <StatusCard data={data} canEdit={canEdit} onSet={setDu} cfg={cfg} />
          {data.booking && (
            <Card title="Buchung" testid="adp-booking-card">
              <div className="text-sm space-y-1">
                {data.order_no && <div className="mb-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2 py-1.5" data-testid="adp-order-no">Auftragsnummer <b>{data.order_no}</b>{data.order_date && <> vom {new Date(data.order_date).toLocaleDateString("de-DE")}</>}{data.order_doc && <div className="mt-1 flex gap-3"><button type="button" onClick={() => downloadBlob(`/documents/${data.order_doc.id}/file`, {}, data.order_doc.filename)} className="underline font-semibold" data-testid="adp-order-doc-download">Auftragsbestätigung (PDF)</button>{data.order_doc.drive_link && <a href={data.order_doc.drive_link} target="_blank" rel="noreferrer" className="underline" data-testid="adp-order-doc-drive">in Google Drive</a>}</div>}</div>}
                <div className="font-medium">{data.booking.format}</div>
                <div className="text-slate-600">{data.booking.price} € netto laut Preisliste</div>
                <div className="text-xs text-slate-500">eingegangen {dt(data.booking.booked_at)}</div>
                {data.booking.price_note && <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-2 py-1.5" data-testid="adp-price-note">Anmerkung des Kunden: {data.booking.price_note}</div>}
                {data.final_price != null && <div className="mt-2 font-semibold text-emerald-700" data-testid="adp-final-price">Vereinbarter Endpreis: {data.final_price} € netto <span className="font-normal text-slate-500">zzgl. MwSt.</span>{data.price_comment && <div className="text-xs font-normal text-slate-500">{data.price_comment}</div>}</div>}
              </div>
            </Card>
          )}
          <InvoiceCard data={data} />
          <UploadCard data={data} onCreate={createLink} onDownload={() => downloadBlob(`/customers-workflow/${issueId}/${id}/file`, {}, data.upload.filename)} />
          <Card title={cfg.one} testid="adp-partner-card">
            <div className="text-sm space-y-1">
              <div>{contact || "–"}</div>
              <div className="text-slate-500">{c.email || "keine E-Mail"}</div>
              <div className="pt-2 text-xs text-slate-500">{cfg.adLabel}</div>
              <div className="font-medium">{c.ad_format || "–"} <span className="text-slate-500 font-normal">{c.ad_size}</span></div>
              {type === "bezahlkunde" && c.amount ? <div className="text-slate-600">{c.amount} € netto</div> : null}
            </div>
          </Card>
        </div>
        <div className="lg:col-span-2 space-y-5">
          <Timeline data={data} />
          <HistoryCard history={data.history} current={issueId} type={type} />
        </div>
      </div>
      <ConfirmPriceDialog row={confirmOpen ? { ...data, name: c.name } : null} onCancel={() => setConfirmOpen(false)} onConfirm={(x) => { setConfirmOpen(false); setDu("zugesagt", x); }} />
      <DeclineDialog customer={decline ? c : null} onCancel={() => setDecline(false)} onConfirm={(x) => { setDecline(false); setDu("abgesagt", x); }} />
      <CustomerMailDialog customers={mailFor} onClose={(done) => { setMailFor(null); if (done) load(); }} />
    </div>
  );
}
